/**
 * @file Pure view component for notes list display
 * @module features/notes/components/NotesList/NotesListView
 * @description
 * Provides flexible rendering options for notes list:
 * - Simple VStack layout for basic display
 * - Standard SectionList for default list view
 * @remarks
 * This component uses helper functions (renderSimpleList, renderDefaultList)
 * to maintain cognitive complexity ≤30 across the two rendering modes (the
 * gorhom-backed bottom-sheet mode was retired in SE-5539).
 * Extracted constants (HEADING_CLASS_DARK, HEADING_CLASS_LIGHT) eliminate duplicate strings.
 */

import { ReactElement, memo } from "react";
import type {
  DefaultSectionT,
  ListRenderItem,
  SectionListData,
  SectionListRenderItem,
} from "react-native";

import { RefreshControl } from "@/components/atoms/RefreshControl";
import { SectionList } from "@/components/atoms/SectionList";

import { Section } from "@/features/notes/utils/groupNotesByDate";
import ItemSeparator from "@/features/notes/components/molecules/ItemSeparator";
import SectionSeparator from "@/features/notes/components/molecules/SectionSeparator";

import { Box } from "@/components/atoms/Box";
import { Heading } from "@/components/atoms/Heading";
import { Stack } from "@/components/atoms/Stack";
import { actionSheetPadding } from "@/config/constants";
import { NotesNoteFragment } from "@/generated/graphql";

/** CSS class for heading text in dark mode */
const HEADING_CLASS_DARK = "text-white";

/** CSS class for heading text in light mode */
const HEADING_CLASS_LIGHT = "text-content-primary";

/** Stub separators object for manual list rendering */
const STUB_SEPARATORS = {
  highlight: () => {},
  unhighlight: () => {},
  updateProps: () => {},
} as const;

/**
 * Creates a section header renderer for the given theme
 * @param isDark - Whether dark theme is active
 * @returns Section header renderer function
 */
function createSectionHeaderRenderer(isDark: boolean) {
  return function renderSectionHeader({
    section: { title },
  }: {
    section: SectionListData<NotesNoteFragment, DefaultSectionT>;
  }): ReactElement {
    return (
      <Heading
        className={`pb-1 text-body ${isDark ? HEADING_CLASS_DARK : HEADING_CLASS_LIGHT}`}
        variant="title-sm"
      >
        {title}
      </Heading>
    );
  };
}

/**
 * Props for NotesListView component
 */
interface Props {
  onEndReached: () => void;
  renderItem:
    | ListRenderItem<NotesNoteFragment>
    | SectionListRenderItem<NotesNoteFragment>;
  refreshing: boolean;
  notes: Section[];
  screenHeight: number;
  isSmallScreen: boolean;
  onRefresh: () => void;
  renderListEmptyComponent: () => ReactElement | null;
  isDark: boolean;
  useSimpleList?: boolean;
}

/**
 * Renders a simple VStack-based list for basic note display
 * @param props - Component properties
 * @param props.sections - Array of note sections grouped by date
 * @param props.renderItem - Function to render each note item
 * @param props.renderListEmptyComponent - Function to render empty state component
 * @param props.isDark - Whether dark theme is active
 * @returns Simple VStack list UI
 */
function renderSimpleList(
  props: Readonly<{
    sections: readonly Section[];
    renderItem:
      | ListRenderItem<NotesNoteFragment>
      | SectionListRenderItem<NotesNoteFragment>;
    renderListEmptyComponent: () => ReactElement | null;
    isDark: boolean;
  }>
) {
  const { sections, renderItem, renderListEmptyComponent, isDark } = props;

  return (
    <Stack className="flex-1">
      {sections.length === 0
        ? renderListEmptyComponent()
        : sections.map(section => (
            <Stack key={section.title} className="gap-3 pb-4">
              <Heading
                className={`text-body ${isDark ? HEADING_CLASS_DARK : HEADING_CLASS_LIGHT}`}
                variant="title-sm"
              >
                {section.title}
              </Heading>
              <Stack className="gap-3">
                {section.data.map((item, itemIndex) => (
                  <Box key={item.id || `note-${itemIndex}`}>
                    {renderItem({
                      item,
                      index: itemIndex,
                      section,
                      separators: STUB_SEPARATORS,
                    })}
                  </Box>
                ))}
              </Stack>
            </Stack>
          ))}
    </Stack>
  );
}

/**
 * Renders a standard SectionList for default note display
 * @param props - Component properties
 * @param props.sections - Array of note sections grouped by date
 * @param props.renderItem - Function to render each note item
 * @param props.renderListEmptyComponent - Function to render empty state component
 * @param props.isDark - Whether dark theme is active
 * @param props.screenHeight - Screen height for sizing the list
 * @param props.refreshing - Whether list is currently refreshing
 * @param props.onRefresh - Handler called when pull-to-refresh is triggered
 * @param props.onEndReached - Handler called when list is scrolled to the end for pagination
 * @returns SectionList UI
 */
function renderDefaultList(
  props: Readonly<{
    sections: readonly Section[];
    renderItem:
      | ListRenderItem<NotesNoteFragment>
      | SectionListRenderItem<NotesNoteFragment>;
    renderListEmptyComponent: () => ReactElement | null;
    isDark: boolean;
    screenHeight: number;
    refreshing: boolean;
    onRefresh: () => void;
    onEndReached: () => void;
  }>
) {
  const {
    sections,
    renderItem,
    renderListEmptyComponent,
    isDark,
    screenHeight,
    refreshing,
    onRefresh,
    onEndReached,
  } = props;

  // design-system: runtime waiver — list height is derived from the runtime
  // screen height and has no closed token; hoisted to a variable so it reaches
  // the raw RN SectionList (import-closure wave) via a single-brace style prop
  // rather than an inline object literal. Behavior unchanged.
  const listStyle = { height: screenHeight - actionSheetPadding };

  return (
    <SectionList
      sections={sections}
      renderSectionHeader={createSectionHeaderRenderer(isDark)}
      // eslint-disable-next-line no-restricted-syntax -- bare re-export SectionList atom has no UNSAFE_style prop; runtime list height derived from screenHeight minus actionSheetPadding, no closed token
      style={listStyle}
      contentContainerClassName={`flex-grow ${!sections?.length ? "justify-center" : ""}`}
      className="p-1"
      SectionSeparatorComponent={SectionSeparator}
      ListEmptyComponent={renderListEmptyComponent}
      stickySectionHeadersEnabled={false}
      ItemSeparatorComponent={ItemSeparator}
      refreshControl={
        <RefreshControl
          refreshing={refreshing}
          onRefresh={onRefresh}
          colors={[isDark ? "white" : "black"]}
          tintColor={isDark ? "white" : "black"}
        />
      }
      refreshing={refreshing}
      onRefresh={onRefresh}
      onEndReached={onEndReached}
      renderItem={renderItem}
    />
  );
}

/**
 * Pure view component for notes list
 * @param root0 - Component props
 * @param root0.onEndReached - Handler called when list is scrolled to the end for pagination
 * @param root0.renderItem - Function to render each note item
 * @param root0.refreshing - Whether list is currently refreshing
 * @param root0.notes - Array of note sections grouped by date
 * @param root0.screenHeight - Screen height for sizing the list
 * @param root0.isSmallScreen - Whether device has small screen size (unused in view)
 * @param root0.onRefresh - Handler called when pull-to-refresh is triggered
 * @param root0.renderListEmptyComponent - Function to render empty state component
 * @param root0.isDark - Whether dark theme is active
 * @param root0.useSimpleList - Whether to use simple VStack list instead of SectionList
 * @remarks Uses helper functions (renderSimpleList, renderDefaultList)
 * to reduce cognitive complexity and improve maintainability
 */
const NotesListView = ({
  onEndReached,
  renderItem,
  refreshing,
  notes,
  screenHeight,
  isSmallScreen: _isSmallScreen,
  onRefresh,
  renderListEmptyComponent,
  isDark,
  useSimpleList = false,
}: Props) =>
  useSimpleList
    ? renderSimpleList({
        sections: notes,
        renderItem,
        renderListEmptyComponent,
        isDark,
      })
    : renderDefaultList({
        sections: notes,
        renderItem,
        renderListEmptyComponent,
        isDark,
        screenHeight,
        refreshing,
        onRefresh,
        onEndReached,
      });

NotesListView.displayName = "NotesListView";

export default memo(NotesListView);
